import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Settings, X } from 'lucide-react';
import type { Notification } from '@nbh/shared';
import { formatDateTime } from '../lib/format';
import { useNotifications } from '../hooks/useNotifications';
import { PushEnableBanner } from './PushEnableBanner';

/**
 * The bell and its drawer (decision D30).
 *
 * Lives in the header rather than the tab bar: it is ambient state, not a destination,
 * and the bottom four tabs are the things a technician goes to on purpose. The count
 * doubles as the PWA app-icon badge — see `lib/appBadge`.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const { feed, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  /**
   * Which items were unread at the moment the drawer opened.
   *
   * Snapshotted because opening marks everything read, and the refresh that follows
   * returns the same rows with `readAt` set — so rendering the dot from the feed would
   * clear every marker the instant the list appeared, and the operator would never see
   * which ones were new. Held until the next open.
   */
  const [wasUnread, setWasUnread] = useState<ReadonlySet<string>>(() => new Set());

  function openDrawer() {
    setWasUnread(new Set(feed.items.filter((i) => i.readAt === null).map((i) => i.id)));
    setOpen(true);
    // Opening it is reading it. Deferred until here rather than on arrival, or the
    // badge would clear for someone who never looked.
    void markAllRead();
  }

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        aria-label={t('notifications.open', { count: feed.unread })}
        className="relative flex size-11 min-h-0 items-center justify-center rounded-lg bg-transparent p-0 text-brookfields-gold-light"
      >
        <Bell className="size-5" aria-hidden="true" />
        {feed.unread > 0 && (
          // Red, not brand gold: this is a count that wants attention, and gold on the
          // green header reads as decoration next to the wordmark it sits beside.
          <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-[18px] text-white">
            {feed.unread > 99 ? '99+' : feed.unread}
          </span>
        )}
      </button>

      {open && <Drawer items={feed.items} wasUnread={wasUnread} onClose={() => setOpen(false)} />}
    </>
  );
}

function Drawer({
  items,
  wasUnread,
  onClose,
}: {
  items: Notification[];
  wasUnread: ReadonlySet<string>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/70">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('notifications.heading')}
        className="flex h-full w-full max-w-sm flex-col border-l border-slate-800 bg-slate-950 pr-[var(--safe-right)]"
      >
        <header className="border-b border-slate-800">
          {/* The drawer overlays the app shell from the top of the viewport, so the
              shell's own status-bar padding does not protect it: under
              black-translucent this row rendered beneath the clock and the battery,
              and the close control was nearly untappable. max(), so a device with
              no inset keeps exactly the 12px it has today. */}
          <div className="flex items-center justify-between px-4 py-3 pt-[max(0.75rem,var(--safe-top))]">
            <p className="text-sm font-semibold text-slate-100">{t('notifications.heading')}</p>
            <Link
              to="/settings/notifications"
              onClick={onClose}
              aria-label={t('settings.heading')}
              className="ml-auto mr-1 flex size-12 min-h-0 items-center justify-center rounded-lg text-slate-400 no-underline"
            >
              <Settings className="size-5" aria-hidden="true" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('notifications.close')}
              className="flex size-12 min-h-0 items-center justify-center bg-transparent p-0 text-slate-400"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>

          {/* Whether alerts are on at all outranks the list underneath it: an empty feed
              and a device that was never subscribed look identical, and only one of them
              is fixable from here. */}
          <PushEnableBanner />
        </header>

        {items.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">{t('notifications.empty')}</p>
        ) : (
          <ul className="flex-1 divide-y divide-slate-800 overflow-y-auto pb-[var(--safe-bottom)]">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!item.url}
                  onClick={() => {
                    if (!item.url) return;
                    onClose();
                    navigate(item.url);
                  }}
                  className="flex min-h-[64px] w-full flex-col gap-0.5 bg-transparent px-4 py-3 text-left disabled:opacity-100"
                >
                  <span className="flex items-center gap-2">
                    {/* From the snapshot, not from `readAt`: opening the drawer marks
                        everything read, so the field is already stale by the time this
                        renders. */}
                    {wasUnread.has(item.id) && (
                      <span
                        className="size-2 shrink-0 rounded-full bg-brookfields-gold"
                        aria-hidden="true"
                      />
                    )}
                    <span className="truncate text-sm font-medium text-slate-100">
                      {item.title}
                    </span>
                  </span>
                  <span className="truncate text-xs text-slate-400">{item.body}</span>
                  <span className="text-xs text-slate-600">{formatDateTime(item.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
